import { useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { isDueDateBeforeStartDate, validatorRequired } from '~src/services/validators';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import ListAddButton from '~src/shared/lists/listAddButton/ListAddButton';
import CustomFormInput from '~src/shared/reactFinalForm/FormInput';
import { FieldType } from '~src/types';
import {
  ButtonContainer,
  CustomDatePickerContainer,
  DateInputContainer,
  FieldContainer,
} from './AddFeedbackMeetingCycleModal.styled';
import { useDispatch } from 'react-redux';
import CustomDatePicker from '~src/shared/form/inputs/CustomDatePicker';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { Segment } from '~src/interfaces/segment.interface';
import moment from 'moment';
import useCreateAdminFeedbackCycle from '~src/hooks/api/adminFeedback/useCreateAdminFeedbackCycle';
import { showNotification } from '~src/store/app/actions';
import { AxiosError } from 'axios';
import feedbackCycleCache from '~src/hooks/api/adminFeedback/cache';
import {useIsoDateFormatter} from "~src/services/hooks/useDateFormatter";

interface FormData {
  name: string;
  userIds: Segment[];
  cycleStartDate: moment.Moment;
  cycleDueDate: moment.Moment;
  meetingStartDate: moment.Moment;
  meetingDueDate: moment.Moment;
}

const AddFeedbackMeetingCycleModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const submitDateFormatter = useIsoDateFormatter();

  const [isOpen, setOpen] = useState(false);
  const isDraft = useRef<boolean | null>(null);

  const { mutate: createAdminFeedbackCycle, error: apiError } = useCreateAdminFeedbackCycle();

  const onCycleCreation = (data: FormData) => {
    createAdminFeedbackCycle(
      {
        name: { en_US: data.name },
        userIds: data.userIds.map(item => item.id),
        active: !isDraft.current,
        cycleStartDate: submitDateFormatter(data.cycleStartDate),
        cycleDueDate: submitDateFormatter(data.cycleDueDate),
        meetingStartDate: submitDateFormatter(data.meetingStartDate),
        meetingDueDate: submitDateFormatter(data.meetingDueDate),
      },
      {
        onSuccess: (responseData) => {
          feedbackCycleCache.addFeedbackCycle(responseData);
          setOpen(false);
          dispatch(
            showNotification({
              id: 'admin_feedback_cycle_created',
              txtKey: isDraft.current
                ? 'x.performance_management.draft.feedback_created'
                : 'x.performance_management.feedback_created',
            }),
          );
        },
      },
    );
  };

  const errorResponse = (
    apiError as AxiosError<
      {
        arguments: {
          field_name: string;
        };
        translation_key: string;
      }[]
    >
  )?.response;

  const notUniqueNameError = errorResponse?.data.find((item) => item.arguments?.field_name.includes('name.'));

  return (
    <>
      <ListAddButton
        addFunc={() => setOpen(true)}
        buttonText={{
          key: 'x.performance-management.add.cycle',
          txt: t('x.performance-management.add.cycle'),
        }}
      />

      <Dialog
        show={isOpen}
        width={800}
        key={`${isOpen}`}
        title={<>{t('x.performance-management.create.cycle')}</>}
        onHide={() => setOpen(false)}
        headerColor="white"
        headerFontColor="black"
      >
        <Form
          onSubmit={onCycleCreation}
          keepDirtyOnReinitialize
          initialValues={{
            name: '',
            userIds: [],
            cycleStartDate: '',
            cycleDueDate: '',
            meetingStartDate: '',
            meetingDueDate: '',
          }}
          render={({ handleSubmit, pristine, values, form }) => (
            <form onSubmit={handleSubmit}>
              <FieldContainer>
                <Field
                  name="name"
                  validate={validatorRequired}
                  label={<>{t('x.performance-management.cycle_name')}</>}
                  children={({ input, label, meta: { error, touched } }: FieldType<string>) => (
                    <CustomFormInput
                      {...input}
                      autoFocus
                      label={label}
                      labelKey={label}
                      type={input.type}
                      error={touched ? (error as string) || notUniqueNameError?.translation_key : undefined}
                    />
                  )}
                />
              </FieldContainer>

              <FieldContainer style={{ marginBottom: '12px' }}>
                <h4 data-translation="x.performance_manager.assigned_groups">
                  <>{t('x.performance_manager.assigned_groups')}</>
                </h4>
                <Field
                  name="userIds"
                  validate={validatorRequired}
                  label={<>{t('x.performance-management.assigned_groups')}</>}
                  children={({ input, label, meta: { error, touched } }: FieldType<Segment[]>) => (
                    <PeopleChips
                      placeHolderKey={`x.survey.performance_manager.chips.placeholder`}
                      fetchSuggestions={fetchSearchSegments}
                      chipsName={label}
                      selectedPeople={input.value || []}
                      onChangePeople={input.onChange}
                      error={touched && !!error}
                    />
                  )}
                />
              </FieldContainer>

              <FieldContainer>
                <h4 data-translation="x.performance_manager.cycle_range.title">
                  <>{t('x.performance_manager.cycle_range.title')}</>
                </h4>
                <DateInputContainer>
                  <Field
                    name="cycleStartDate"
                    validate={(value: moment.Moment) => {
                      if (!value) {
                        return validatorRequired(value) as string;
                      }
                      return isDueDateBeforeStartDate(value, values.cycleDueDate);
                    }}
                    label={<>{t('x.performance_manager.cycle_range.start')}</>}
                    children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                      <CustomDatePickerContainer>
                        <CustomDatePicker
                          {...input}
                          dateValue={input.value}
                          error={touched ? (error as string) : undefined}
                          allowClear={false}
                          label={label}
                          onChange={(value) => {
                            input.onChange(value);
                            form.resetFieldState('cycleDueDate');
                          }}
                          labelKey={label}
                          datePickerOptions={{
                            hideDisabledOptions: true,
                            disabledDate: (d) =>
                              d.toDate().getTime() > moment().add(10, 'years').toDate().getTime(),
                          }}
                        />
                      </CustomDatePickerContainer>
                    )}
                  />
                  <Field
                    name="cycleDueDate"
                    validate={(value: moment.Moment) => {
                      if (!value) {
                        return validatorRequired(value) as string;
                      }
                      return isDueDateBeforeStartDate(values.cycleStartDate, value);
                    }}
                    label={<>{t('x.performance_manager.cycle_range.due')}</>}
                    children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                      <CustomDatePickerContainer>
                        <CustomDatePicker
                          {...input}
                          dateValue={input.value}
                          error={touched ? (error as string) : undefined}
                          allowClear={false}
                          label={label}
                          onChange={(value) => {
                            input.onChange(value);
                            form.resetFieldState('cycleStartDate');
                          }}
                          labelKey={label}
                          datePickerOptions={{
                            hideDisabledOptions: true,
                            disabledDate: (d) =>
                              d.toDate().getTime() > moment().add(10, 'years').toDate().getTime(),
                          }}
                        />
                      </CustomDatePickerContainer>
                    )}
                  />
                </DateInputContainer>
              </FieldContainer>
              <FieldContainer>
                <h4 data-translation="x.performance_manager.meeting_range.title">
                  <>{t('x.performance_manager.meeting_range.title')}</>
                </h4>
                <DateInputContainer>
                  <Field
                    name="meetingStartDate"
                    validate={(value: moment.Moment) => {
                      if (!value) {
                        return validatorRequired(value) as string;
                      }
                      return isDueDateBeforeStartDate(value, values.meetingDueDate);
                    }}
                    label={<>{t('x.performance_manager.meeting_range.start')}</>}
                    children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                      <CustomDatePickerContainer>
                        <CustomDatePicker
                          {...input}
                          dateValue={input.value}
                          error={touched ? (error as string) : undefined}
                          allowClear={false}
                          onChange={(value) => {
                            input.onChange(value);
                            form.resetFieldState('meetingDueDate');
                          }}
                          label={label}
                          labelKey={label}
                          datePickerOptions={{
                            disabledDate: (d) => d.toDate().getTime() < moment().toDate().getTime(),
                          }}
                        />
                      </CustomDatePickerContainer>
                    )}
                  />
                  <Field
                    name="meetingDueDate"
                    validate={(value: moment.Moment) => {
                      if (!value) {
                        return validatorRequired(value) as string;
                      }
                      return isDueDateBeforeStartDate(values.meetingStartDate, value);
                    }}
                    label={<>{t('x.performance_manager.meeting_range.due')}</>}
                    children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                      <CustomDatePickerContainer>
                        <CustomDatePicker
                          {...input}
                          dateValue={input.value}
                          error={touched ? (error as string) : undefined}
                          allowClear={false}
                          label={label}
                          onChange={(value) => {
                            input.onChange(value);
                            form.resetFieldState('meetingStartDate');
                          }}
                          labelKey={label}
                          datePickerOptions={{
                            disabledDate: (d) => d.toDate().getTime() < moment().toDate().getTime(),
                          }}
                        />
                      </CustomDatePickerContainer>
                    )}
                  />
                </DateInputContainer>
              </FieldContainer>

              <ButtonContainer>
                <SecondaryButton
                  type="submit"
                  style={{ marginRight: '10px' }}
                  disabled={pristine}
                  onClick={() => {
                    isDraft.current = true;
                  }}
                >
                  <span data-translation="x.surveys.schedules.saveas_draft_button">
                    <>{t('x.surveys.schedules.saveas_draft_button')}</>
                  </span>
                </SecondaryButton>
                <PrimaryButtonLoading
                  type="submit"
                  onClick={() => {
                    isDraft.current = false;
                  }}
                  disabled={pristine}
                  isLoading={false}
                >
                  <span data-translation="x.button.activate">
                    <>{t('x.button.activate')}</>
                  </span>
                </PrimaryButtonLoading>
              </ButtonContainer>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

export default AddFeedbackMeetingCycleModal;
